import React, {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { isNil } from 'lodash';
import useEntities from 'new/state/entities/repository/useEntities';
import { Container, Slide, styled } from '@mui/material';
import Portal from 'new/elements/Portal';
import { PipelineItems } from 'js/components/pipelines/FlowNodes/mappers';
import { OrchestrationContext } from 'js/components/pipelines/FlowNodes/context/OrchestrationContext';
import ViewDataSinksContainer from 'js/components/data_sinks/ViewDataSinksContainer';
import { Button } from 'new/elements/Button';

const StyledContainer = styled(Container)({
  position: 'fixed',
  minWidth: 700,
  width: '80%',
  height: '100%',
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  zIndex: 1101,
  backgroundColor: 'white',
  boxShadow: '0 1px 0 0 rgba(91, 101, 117, 0.15)',
});

export const ResourceOrchestrationProvider = (props: PropsWithChildren<any>) => {
  const { children } = props;
  const [selectedCardData, setSelected] = useState(null);
  const [selection, setSelection] = useState([]);

  const { data: entities } = useEntities();
  const { sources, sinks, dataSets } = entities;

  const selectedCardEntity = _getSelectedCardEntity();
  const open = !isNil(selectedCardEntity);

  const onClickOrchestrate = useCallback((selectedCardData) => {
    setSelected(selectedCardData);
  }, []);

  const onConfirmSelection = useCallback(() => {
    setSelected(null);
    setSelection([]);
  }, []);

  const onSelectionChange = useCallback((selection) => {
    setSelection(selection);
  }, []);

  function _getSelectedCardEntity() {
    if (!selectedCardData) {
      return null;
    }

    switch (selectedCardData.type) {
      case 'SOURCE':
        return sources[selectedCardData.id];

      case 'DATA_SINK':
        return sinks[selectedCardData.id];

      default:
        return dataSets[selectedCardData.id];
    }
  }

  function _handleCloseDetailClicked() {
    setSelected(null);
  }

  const value = useMemo(() => {
    return { onClickOrchestrate };
  }, [onClickOrchestrate]);

  return (
    <OrchestrationContext.Provider value={value}>
      {children}
      {selectedCardEntity ? (
        <Portal>
          <div className="modal-fader" onClick={_handleCloseDetailClicked} />
          <Slide direction="left" in={open}>
            <StyledContainer disableGutters>
              <ViewDataSinksContainer
                title="Select one or more Destinations as triggers"
                description="When the processing for the selected destinations are complete your source processing will be triggered"
                isSelection
                rightView={
                  <Button
                    variant="ghost"
                    color="secondary"
                    onClick={onConfirmSelection}
                    disabled={selection.length === 0}
                  >
                    Confirm Selection
                  </Button>
                }
                onSelectionChange={onSelectionChange}
              />
            </StyledContainer>
          </Slide>
        </Portal>
      ) : null}
    </OrchestrationContext.Provider>
  );
};
